import { AlertTriangle, Trash2, X } from 'lucide-react';
import { useDeleteTour } from '../hooks/useDeleteTour';
import { useState } from 'react';

export const DeleteConfirmModal = (props) => {
  const { isDeleteModalOpen, setIsDeleteModalOpen, tour } = props;
  const { mutate: deleteTour, isPending } = useDeleteTour();
  const [hardDelete, setHardDelete] = useState(false);

  const handleDelete = () => {
    deleteTour(
      {
        tourId: tour._id,
        hard: hardDelete,
      },
      {
        onSuccess: () => {
          setIsDeleteModalOpen(false);
          setHardDelete(false);
        },
      }
    );
  };

  if (!isDeleteModalOpen || !tour) return null;

  return (
    <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4'>
      <div className='bg-card border border-border rounded-lg w-full max-w-md'>
        {/* Header */}
        <div className='px-6 py-4 border-b border-border flex items-center justify-between'>
          <div className='flex items-center space-x-3'>
            <div className='p-2 bg-destructive/10 rounded-lg'>
              <AlertTriangle size={20} className='text-destructive' />
            </div>
            <h2 className='text-lg font-semibold'>Delete Tour Package</h2>
          </div>
          <button
            onClick={() => setIsDeleteModalOpen(false)}
            className='p-1 hover:bg-muted rounded-md transition-colors'
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className='p-6 space-y-4'>
          {/* Tour Info */}
          <div className='bg-muted rounded-lg p-4'>
            <h3 className='text-sm font-medium mb-2'>{tour.title}</h3>
            <div className='grid grid-cols-2 gap-2 text-xs text-muted-foreground'>
              <div>
                <span>Destination:</span>
                <span className='ml-1 font-medium text-foreground'>{tour.destination}</span>
              </div>
              <div>
                <span>Duration:</span>
                <span className='ml-1 font-medium text-foreground'>
                  {tour.duration} {tour.duration === 1 ? 'day' : 'days'}
                </span>
              </div>
              <div>
                <span>Seller:</span>
                <span className='ml-1 font-medium text-foreground'>
                  {tour.sellerId?.firstName || tour.sellerId?.lastName
                    ? `${tour.sellerId?.firstName || ''} ${tour.sellerId?.lastName || ''}`.trim()
                    : tour.sellerId?.phoneNumber || 'N/A'}
                </span>
              </div>
              <div>
                <span>Bookings:</span>
                <span className='ml-1 font-medium text-foreground'>{tour.stats?.totalBookings || 0}</span>
              </div>
            </div>
          </div>

          {/* Warning Message */}
          <div className='bg-destructive/10 border border-destructive/20 rounded-lg p-4'>
            <p className='text-sm text-destructive font-medium mb-2'>
              {hardDelete ? '⚠️ Permanent Deletion Warning' : 'Soft Delete'}
            </p>
            <p className='text-sm text-muted-foreground'>
              {hardDelete
                ? 'This action cannot be undone. The tour package will be permanently removed from the database along with all associated data.'
                : 'The tour package will be marked as deleted and hidden from listings. It can be restored later if needed.'}
            </p>
          </div>

          {/* Hard Delete Toggle */}
          <div className='flex items-center justify-between p-3 bg-muted rounded-lg'>
            <div>
              <p className='text-sm font-medium'>Permanent Delete</p>
              <p className='text-xs text-muted-foreground'>Permanently remove from database</p>
            </div>
            <label className='relative inline-flex items-center cursor-pointer'>
              <input
                type='checkbox'
                checked={hardDelete}
                onChange={(e) => setHardDelete(e.target.checked)}
                className='sr-only peer'
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-destructive/20 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-destructive"></div>
            </label>
          </div>

          {/* Statistics Warning (if has bookings) */}
          {tour.stats?.totalBookings > 0 && (
            <div className='bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3'>
              <div className='flex items-start gap-2'>
                <AlertTriangle size={16} className='text-yellow-600 dark:text-yellow-400 mt-0.5' />
                <div>
                  <p className='text-sm font-medium text-yellow-600 dark:text-yellow-400'>Active Bookings</p>
                  <p className='text-xs text-muted-foreground mt-1'>
                    This tour has {tour.stats.totalBookings} booking(s). Please ensure all bookings are handled before
                    deletion.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className='flex justify-end space-x-3 px-6 py-4 border-t border-border'>
          <button
            onClick={() => {
              setIsDeleteModalOpen(false);
              setHardDelete(false);
            }}
            className='px-4 py-2 text-sm border border-border rounded-md hover:bg-accent transition-colors'
            disabled={isPending}
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={isPending}
            className='flex items-center space-x-2 px-4 py-2 text-sm bg-destructive text-destructive-foreground rounded-md hover:bg-destructive/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
          >
            <Trash2 size={16} />
            <span>{isPending ? 'Deleting...' : hardDelete ? 'Delete Permanently' : 'Delete'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};
